import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { AlertCircle, Loader2, ShieldCheck, KeyRound } from 'lucide-react';

const LoginPage = () => {
    const [step, setStep] = useState('credentials'); // credentials | mfa_setup | mfa_code | change_password
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // MFA state
    const [pendingToken, setPendingToken] = useState('');
    const [qrCode, setQrCode] = useState('');
    const [secret, setSecret] = useState('');
    const [code, setCode] = useState('');
    const [rememberDevice, setRememberDevice] = useState(true);

    // Change password state
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // Recovery codes state
    const [recoveryCodes, setRecoveryCodes] = useState(null);
    const [mfaData, setMfaData] = useState(null);
    const [recoverCode, setRecoverCode] = useState('');

    const { login, completeMfa, completeMfaRecover, activateStoredSession, finishPasswordChange } = useAuth();
    const navigate = useNavigate();

    const finalizeAndGo = (data) => {
        if (data.user?.must_change_password) {
            setStep('change_password');
        } else {
            activateStoredSession();
            navigate('/dashboard');
        }
    };

    const handleCredentials = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const data = await login(email, password);
            if (data.mfa_setup_required) {
                setPendingToken(data.pending_token);
                setQrCode(data.qr_code);
                setSecret(data.secret);
                setStep('mfa_setup');
            } else if (data.mfa_required) {
                setPendingToken(data.pending_token);
                setStep('mfa_code');
            } else if (data.token) {
                finalizeAndGo(data);
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore durante il login');
        } finally {
            setLoading(false);
        }
    };

    const handleMfaVerify = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const data = await completeMfa(pendingToken, code, rememberDevice);
            if (data.recovery_codes) {
                setMfaData(data);
                setRecoveryCodes(data.recovery_codes);
                setStep('show_recovery_codes');
            } else {
                finalizeAndGo(data);
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Codice non valido');
            setCode('');
        } finally {
            setLoading(false);
        }
    };

    const handleRecoverCode = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const data = await completeMfaRecover(pendingToken, recoverCode);
            finalizeAndGo(data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Codice di recupero non valido');
            setRecoverCode('');
        } finally {
            setLoading(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setError('');
        if (newPassword.length < 8) {
            setError('La nuova password deve avere almeno 8 caratteri');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Le password non coincidono');
            return;
        }
        setLoading(true);
        try {
            await authAPI.changePassword(password, newPassword);
            await finishPasswordChange();
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore nel cambio password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="login-bg min-h-screen flex items-center justify-center p-4"
            style={{ backgroundImage: `linear-gradient(to right, rgba(38, 50, 56, 0.72), rgba(38, 50, 56, 0.45)), url(${process.env.PUBLIC_URL}/login-bg.webp)` }}
        >
            <div className="w-full max-w-md animate-fade-in">
                <Card className="border-0 shadow-2xl">
                    <CardHeader className="space-y-1 pb-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-primary rounded-sm flex items-center justify-center">
                                <span className="text-white font-heading text-xl font-bold">P</span>
                            </div>
                            <div>
                                <CardTitle className="font-heading text-2xl uppercase tracking-tight">
                                    Pezzaioli CRM
                                </CardTitle>
                                <CardDescription className="text-xs uppercase tracking-wider">
                                    Sistema Gestione Commerciale
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {error && (
                            <div className="flex items-center gap-2 p-3 mb-4 bg-destructive/10 text-destructive rounded-sm text-sm" data-testid="login-error">
                                <AlertCircle size={16} />
                                <span>{error}</span>
                            </div>
                        )}

                        {step === 'credentials' && (
                            <form onSubmit={handleCredentials} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-xs uppercase tracking-wider font-medium">Email</Label>
                                    <Input id="email" type="email" placeholder="nome@pezzaioli.it" value={email}
                                        onChange={(e) => setEmail(e.target.value)} required className="h-11" data-testid="login-email" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="password" className="text-xs uppercase tracking-wider font-medium">Password</Label>
                                    <Input id="password" type="password" placeholder="••••••••" value={password}
                                        onChange={(e) => setPassword(e.target.value)} required className="h-11" data-testid="login-password" />
                                </div>
                                <Button type="submit" className="w-full h-11 btn-primary font-heading text-sm" disabled={loading} data-testid="login-submit">
                                    {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Accesso in corso...</>) : 'ACCEDI'}
                                </Button>
                            </form>
                        )}

                        {step === 'show_recovery_codes' && (
                            <div className="space-y-4" data-testid="recovery-codes-step">
                                <div className="flex items-center gap-2 text-sm font-medium">
                                    <KeyRound size={18} className="text-primary" />
                                    Salva i tuoi codici di recupero
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Conserva questi <b>8 codici monouso</b> in un luogo sicuro. Se perdi il telefono, ti serviranno per rientrare. Vengono mostrati <b>una sola volta</b>.
                                </p>
                                <div className="grid grid-cols-2 gap-2 p-3 bg-muted/30 rounded-sm border font-mono text-sm" data-testid="recovery-codes-list">
                                    {(recoveryCodes || []).map((rc, i) => (
                                        <div key={i} className="tracking-wider text-center py-1">{rc}</div>
                                    ))}
                                </div>
                                <Button type="button" variant="outline" className="w-full"
                                    onClick={() => { navigator.clipboard?.writeText((recoveryCodes || []).join('\n')); }}
                                    data-testid="recovery-codes-copy">
                                    Copia i codici
                                </Button>
                                <Button type="button" className="w-full h-11 btn-primary font-heading text-sm"
                                    onClick={() => finalizeAndGo(mfaData)} data-testid="recovery-codes-continue">
                                    HO SALVATO I CODICI, CONTINUA
                                </Button>
                            </div>
                        )}

                        {step === 'recover_code' && (
                            <form onSubmit={handleRecoverCode} className="space-y-4" data-testid="recover-code-step">
                                <div className="flex items-center gap-2 text-sm font-medium">
                                    <KeyRound size={18} className="text-primary" />
                                    Accedi con un codice di recupero
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Inserisci uno dei codici di recupero salvati durante la configurazione dell'MFA. Dopo l'accesso dovrai riconfigurare l'app authenticator.
                                </p>
                                <div className="space-y-2">
                                    <Label htmlFor="recCode" className="text-xs uppercase tracking-wider font-medium">Codice di recupero</Label>
                                    <Input id="recCode" placeholder="XXXX-XXXX" value={recoverCode}
                                        onChange={(e) => setRecoverCode(e.target.value)} required autoFocus
                                        className="h-11 text-center tracking-widest font-mono" data-testid="recover-code-input" />
                                </div>
                                <Button type="submit" className="w-full h-11 btn-primary font-heading text-sm" disabled={loading} data-testid="recover-code-submit">
                                    {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verifica...</>) : 'ACCEDI'}
                                </Button>
                                <button type="button" onClick={() => { setError(''); setRecoverCode(''); setStep('mfa_code'); }}
                                    className="w-full text-center text-xs text-muted-foreground hover:text-primary transition-colors" data-testid="recover-code-cancel">
                                    Annulla
                                </button>
                            </form>
                        )}

                        {step === 'mfa_setup' && (
                            <form onSubmit={handleMfaVerify} className="space-y-4" data-testid="mfa-setup-step">
                                <div className="flex items-center gap-2 text-sm font-medium">
                                    <ShieldCheck size={18} className="text-primary" />
                                    Configura l'autenticazione a due fattori
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Scansiona questo QR code con Google Authenticator o Microsoft Authenticator,
                                    poi inserisci il codice a 6 cifre generato dall'app.
                                </p>
                                {qrCode && (
                                    <div className="flex justify-center">
                                        <img src={qrCode} alt="QR code MFA" className="w-44 h-44 border rounded" data-testid="mfa-qr" />
                                    </div>
                                )}
                                {secret && (
                                    <p className="text-[11px] text-center text-muted-foreground">
                                        Chiave manuale: <code className="bg-muted px-1 rounded break-all" data-testid="mfa-secret">{secret}</code>
                                    </p>
                                )}
                                <div className="space-y-2">
                                    <Label htmlFor="code" className="text-xs uppercase tracking-wider font-medium">Codice a 6 cifre</Label>
                                    <Input id="code" inputMode="numeric" maxLength={6} placeholder="000000" value={code}
                                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} required
                                        className="h-11 text-center tracking-[0.4em] text-lg" data-testid="mfa-code" />
                                </div>
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <Checkbox checked={rememberDevice} onCheckedChange={(v) => setRememberDevice(!!v)} data-testid="mfa-remember" />
                                    Ricorda questo dispositivo per 30 giorni
                                </label>
                                <Button type="submit" className="w-full h-11 btn-primary font-heading text-sm" disabled={loading} data-testid="mfa-verify-submit">
                                    {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verifica...</>) : 'ATTIVA E ACCEDI'}
                                </Button>
                            </form>
                        )}

                        {step === 'mfa_code' && (
                            <form onSubmit={handleMfaVerify} className="space-y-4" data-testid="mfa-code-step">
                                <div className="flex items-center gap-2 text-sm font-medium">
                                    <ShieldCheck size={18} className="text-primary" />
                                    Verifica in due passaggi
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Inserisci il codice a 6 cifre dalla tua app authenticator.
                                </p>
                                <div className="space-y-2">
                                    <Label htmlFor="code" className="text-xs uppercase tracking-wider font-medium">Codice a 6 cifre</Label>
                                    <Input id="code" inputMode="numeric" maxLength={6} placeholder="000000" value={code}
                                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} required autoFocus
                                        className="h-11 text-center tracking-[0.4em] text-lg" data-testid="mfa-code" />
                                </div>
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <Checkbox checked={rememberDevice} onCheckedChange={(v) => setRememberDevice(!!v)} data-testid="mfa-remember" />
                                    Ricorda questo dispositivo per 30 giorni
                                </label>
                                <Button type="submit" className="w-full h-11 btn-primary font-heading text-sm" disabled={loading} data-testid="mfa-verify-submit">
                                    {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verifica...</>) : 'VERIFICA E ACCEDI'}
                                </Button>
                                <button type="button" onClick={() => { setError(''); setStep('recover_code'); }}
                                    className="w-full text-center text-xs text-muted-foreground hover:text-primary transition-colors" data-testid="use-recovery-code-link">
                                    Non riesci ad accedere? Usa un codice di recupero
                                </button>
                            </form>
                        )}

                        {step === 'change_password' && (
                            <form onSubmit={handleChangePassword} className="space-y-4" data-testid="change-password-step">
                                <div className="flex items-center gap-2 text-sm font-medium">
                                    <KeyRound size={18} className="text-primary" />
                                    Imposta una nuova password
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Per motivi di sicurezza devi impostare una nuova password personale.
                                </p>
                                <div className="space-y-2">
                                    <Label htmlFor="newPassword" className="text-xs uppercase tracking-wider font-medium">Nuova password</Label>
                                    <Input id="newPassword" type="password" placeholder="Almeno 8 caratteri" value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)} required className="h-11" data-testid="new-password" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword" className="text-xs uppercase tracking-wider font-medium">Conferma password</Label>
                                    <Input id="confirmPassword" type="password" placeholder="Ripeti la password" value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)} required className="h-11" data-testid="confirm-password" />
                                </div>
                                <Button type="submit" className="w-full h-11 btn-primary font-heading text-sm" disabled={loading} data-testid="change-password-submit">
                                    {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Salvataggio...</>) : 'SALVA E CONTINUA'}
                                </Button>
                            </form>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default LoginPage;
